import { useCallback, useMemo, useRef, useState } from 'react';
import { CARD_LINKED_OFFERS_TRANSACTION_URL } from 'common/api/config';
import { APIResponse } from 'common/api/models';
import { monetizeWithDecimal } from 'common/utils/strings';
import { withTokensIfNeeded } from 'common/api/utils/withTokensIfNeeded';

// TODO: remove these mock entries
import { mockResponses } from './__fixtures__/mockLocalDiningHistoryResponses';

const PAGE_SIZE = 20;

export interface LocalDiningHistoryRecord {
    merchantName: string;
    organizationBenefitAmount: string;
    transactionAmount: string;
    transactionDateTime: string;
    transactionId: string;
}

function mockApi(start: string, end: string): Promise<LocalDiningHistoryRecord[]> {
    return new Promise((resolve, reject) => {
        setTimeout(() => {
            // TODO: mock a bad response?
            const limit = Math.random() / 2;
            const result = mockResponses
                .filter(() => Math.random() < limit)
                .sort(() => Math.random() - 0.5);
            resolve(result);
        }, Math.random() * 1500 + 1000);
    });

    // return Promise.reject(new Error('example error'));
}

const requestOrderHistory = async (start: string, end: string) => {
    const _start = start.split('T')[0];
    const _end = end.split('T')[0];

    const request = await withTokensIfNeeded<LocalDiningHistoryRecord[]>(
        `${CARD_LINKED_OFFERS_TRANSACTION_URL}?startDate=${_start}&endDate=${_end}&api-version=1`
    );

    return request;
};

export function useLocalDiningHistory() {
    const [state, setState] = useState<{
        loading: boolean;
        fullResults: LocalDiningHistoryRecord[];
        uiResults: LocalDiningHistoryRecord[];
        canLoadMoreResults: boolean;
        error: null | string;
    }>({
        loading: false,
        fullResults: [],
        error: null,
        uiResults: [],
        canLoadMoreResults: false,
    });
    const latestApiRequest = useRef<Promise<APIResponse<LocalDiningHistoryRecord[], null>>>();

    const setViewedDateRange = useCallback(
        (range: { start: string; end: string } | null): Promise<LocalDiningHistoryRecord[]> => {
            if (!range) {
                setState({
                    loading: false,
                    fullResults: [],
                    error: null,
                    uiResults: [],
                    canLoadMoreResults: false,
                });
                return Promise.resolve([]);
            }

            const { start, end } = range;
            if (end < start) {
                setState({
                    loading: false,
                    fullResults: [],
                    error: null,
                    uiResults: [],
                    canLoadMoreResults: false,
                });
                return Promise.resolve([]);
            }

            setState({
                loading: true,
                fullResults: [],
                error: null,
                uiResults: [],
                canLoadMoreResults: false,
            });
            const apiRequest = requestOrderHistory(start, end);
            latestApiRequest.current = apiRequest;

            return apiRequest
                .then((response) => {
                    if (apiRequest === latestApiRequest.current) {
                        if (response.data && response.data.length > 0) {
                            const results = response.data.map((d) => ({
                                ...d,
                                transactionAmount: monetizeWithDecimal(
                                    d.transactionAmount as unknown as number
                                ),
                                organizationBenefitAmount: monetizeWithDecimal(
                                    d.organizationBenefitAmount as unknown as number
                                ),
                            }));

                            const uiResults = results.slice(0, PAGE_SIZE);
                            setState({
                                loading: false,
                                fullResults: results,
                                uiResults,
                                error: null,
                                canLoadMoreResults: results.length > PAGE_SIZE,
                            });
                        } else {
                            setState({
                                loading: false,
                                fullResults: [],
                                error: null,
                                uiResults: [],
                                canLoadMoreResults: false,
                            });
                        }
                    }
                    return state.fullResults;
                })
                .catch((errorMessage) => {
                    setState({
                        loading: false,
                        fullResults: [],
                        error: errorMessage,
                        uiResults: [],
                        canLoadMoreResults: false,
                    });
                    return [];
                });
        },
        [mockApi]
    );

    const loadMore = useCallback(() => {
        const nextStartIndex = state.uiResults.length;
        const nextEndIndex = nextStartIndex + PAGE_SIZE;

        const previous = state.uiResults;
        const nextSegment = state.fullResults.slice(nextStartIndex, nextEndIndex);
        const nextUiResults = [...previous, ...nextSegment];

        setState((prev) => ({
            ...prev,
            uiResults: nextUiResults,
            canLoadMoreResults: prev.fullResults.length > nextUiResults.length,
        }));
    }, [state, setState]);

    const result = useMemo(
        () => ({
            setViewedDateRange,
            loadMore,
            canLoadMoreResults: state.canLoadMoreResults,
            loading: state.loading,
            results: state.uiResults,
            error: state.error,
        }),
        [state, setViewedDateRange]
    );

    return result;
}
